import { ReactNode, useState } from 'react';
import { Mode } from './Header';

// 첫 페이지(홈) — 줍줍이 뭐 하는 서비스인지 + 각 화면 빠른 이동 + 폰 앱 설치/알림 안내.
// 처음 들어온 사람이 길을 잃지 않게. 모바일(PWA)에서 가장 많이 보므로 모바일 우선 레이아웃.

const QUICK: { id: Mode; emoji: string; title: string; desc: string }[] = [
  { id: 'daytrade',  emoji: '⚡', title: '단타',       desc: '큰손 수급·매수 시작 알림 + 오늘의 큰손 + 🌙 밤새 보유 판단' },
  { id: 'market',    emoji: '📈', title: '시장',       desc: '오늘 장 지형도 — 위치·수급 흐름·공포탐욕·시나리오' },
  { id: 'scanner',   emoji: '🔍', title: '전체 스캔',   desc: '전 종목 추세 점수·RS 랭킹 (좋은 종목 발굴)' },
  { id: 'watchlist', emoji: '📋', title: '관심종목',    desc: '담은 종목 점수·시그널 + 보유손익 관리' },
  { id: 'mystatus',  emoji: '🏠', title: '내 현황',     desc: '보유 × 시장국면 교차 + AI 브리핑·추천·어드바이저' },
  { id: 'themes',    emoji: '🔥', title: '주도 테마',   desc: '지금 시장이 어느 테마 중심으로 도는지' },
  { id: 'radar',     emoji: '🛰️', title: '글로벌 레이더', desc: '미국 섹터 → 오늘 아침 한국 테마 연결' },
];

// 🎨 규격 V2(08-23) 공용 부품 — 섹션 라벨은 키커, 타일은 헤어라인 테두리만(그림자·보라 은퇴).
const KICKER = 'text-[10.5px] font-[650] tracking-[.12em] text-dc-ink3';
const TILE = 'border border-dc-line1 rounded-lg hover:border-dc-point/40 hover:bg-white/[0.015] transition-colors';

function StepList({ steps }: { steps: ReactNode[] }) {
  return (
    <ol className="space-y-1.5">
      {steps.map((s, i) => (
        <li key={i} className="flex gap-2 text-dc-ink2 text-[13px] leading-relaxed">
          <span className="shrink-0 w-5 h-5 rounded-full border border-dc-line1 bg-dc-bg1 text-dc-ink3 text-[10.5px] font-[650] flex items-center justify-center mt-0.5">
            {i + 1}
          </span>
          <span className="min-w-0">{s}</span>
        </li>
      ))}
    </ol>
  );
}

export default function HomePage({ onTab }: { onTab: (m: Mode) => void }) {
  const [showInstall, setShowInstall] = useState(true);

  return (
    <div className="flex-1 overflow-auto">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-6">

        {/* 히어로 */}
        <div className="flex items-center gap-4">
          <img src="/logo192.png?v=2" alt="줍줍" className="w-14 h-14 rounded-lg shrink-0" />
          <div className="min-w-0">
            <h1 className="text-[27px] font-[760] tracking-[-.015em] text-dc-ink1">줍줍 <span className="text-dc-point text-[15.5px] font-medium">JUPJUP</span></h1>
            <p className="text-dc-ink2 text-[15.5px] leading-[1.55] mt-1">추세는 길게, 단타는 빠르게 — 저점을 줍줍.</p>
          </div>
        </div>

        <p className="text-dc-ink2 text-[13px] leading-[1.6] border-b border-dc-hair pb-5">
          KOSPI·KOSDAQ·미국(S&P500)을 <b className="text-dc-ink1">추세추종 점수</b>로 줄세워 좋은 종목을 발굴하고,
          종목별 <b className="text-dc-ink1">AI 심층분석</b>과 장중 <b className="text-dc-ink1">단타 자리·알림</b>까지 한곳에서 봅니다.
          점수는 <b className="text-dc-ink1">후보 선별 도구</b>지 매수 명령이 아니에요 — 손절 규율·시장국면과 함께 보세요.
        </p>

        {/* 빠른 시작 */}
        <div>
          <h2 className={`${KICKER} mb-2.5`}>바로 가기</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {QUICK.map((q) => (
              <button
                key={q.id}
                onClick={() => onTab(q.id)}
                className={`text-left p-3 ${TILE}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[15.5px] leading-none shrink-0">{q.emoji}</span>
                  <span className="text-dc-ink1 text-[13px] font-bold">{q.title}</span>
                  <span className="ml-auto text-dc-ink3 text-[11.5px]">→</span>
                </div>
                <p className="text-dc-ink2 text-[11.5px] leading-snug">{q.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* 📱 폰에 앱 설치 */}
        <div className="border border-dc-line1 rounded-lg overflow-hidden">
          <button
            onClick={() => setShowInstall((v) => !v)}
            className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-white/[0.015] transition-colors"
          >
            <span className="text-[15.5px] leading-none shrink-0">📱</span>
            <span className="text-dc-ink1 text-[13px] font-bold">폰에 앱으로 설치하기 + 알림 받기</span>
            <span className="ml-auto text-dc-point text-[11.5px]">{showInstall ? '▾ 접기' : '▸ 펼치기'}</span>
          </button>

          {showInstall && (
            <div className="px-4 pb-4 space-y-4">
              <p className="text-dc-ink2 text-[11.5px] leading-relaxed">
                앱스토어 앱이 아니라 <b className="text-dc-ink1">웹을 홈 화면 아이콘으로 얹는 방식</b>(PWA)이라 1분이면 끝나요.
                설치하면 <b className="text-dc-ink1">잠금화면 푸시 알림</b>(큰손 수급·큰손 매수/매도 시작, 핀 손절·목표, 감시 종목)을 받을 수 있어요.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* 안드로이드 */}
                <div className="rounded-lg border border-dc-line1 bg-dc-bg1/60 p-3">
                  <p className="text-dc-ink1 text-[11.5px] font-bold mb-2">🤖 안드로이드 (갤럭시·픽셀)</p>
                  <StepList steps={[
                    <><b className="text-dc-ink1">Chrome</b>으로 이 사이트 열기 <span className="text-dc-ink3">(삼성인터넷·웨일보다 Chrome 권장)</span></>,
                    <>아래 <b className="text-dc-ink1">"앱 설치"</b> 배너 누르기 <span className="text-dc-ink3">— 없으면 ⋮(점 3개) → "앱 설치"</span></>,
                    <>홈 화면에 <b className="text-dc-ink1">줍줍 아이콘</b> 생성 완료</>,
                  ]} />
                </div>
                {/* 아이폰 */}
                <div className="rounded-lg border border-dc-line1 bg-dc-bg1/60 p-3">
                  <p className="text-dc-ink1 text-[11.5px] font-bold mb-2">🍎 아이폰·아이패드</p>
                  <p className="text-dc-warn text-[11.5px] mb-2 leading-snug">⚠️ 애플 정책상 <b>Safari로 설치</b>해야 알림이 와요.</p>
                  <StepList steps={[
                    <><b className="text-dc-ink1">Safari</b>로 이 사이트 열기</>,
                    <>아래 가운데 <b className="text-dc-ink1">공유 버튼</b>(□↑) 누르기</>,
                    <><b className="text-dc-ink1">"홈 화면에 추가"</b> → "추가"</>,
                  ]} />
                </div>
              </div>

              {/* 알림 켜기 */}
              <div className="rounded-lg border border-dc-point/25 bg-dc-point/[0.04] px-3.5 py-3">
                <p className="text-dc-point text-[11.5px] font-bold mb-2">🔔 알림 켜기 (설치 후 한 번만)</p>
                <StepList steps={[
                  <>홈 화면의 <b className="text-dc-ink1">줍줍 아이콘</b>으로 앱 열기 <span className="text-dc-ink3">(브라우저 탭 말고 설치된 앱으로!)</span></>,
                  <><button onClick={() => onTab('daytrade')} className="text-dc-point underline underline-offset-2 hover:text-dc-point/80"><b>단타</b> 탭</button> → 우측 상단 <b className="text-dc-ink1">🔔 버튼</b> 누르기</>,
                  <>폰이 물으면 <b className="text-dc-ink1">"허용"</b> → 끝. 폰이 잠겨 있어도 알림이 옵니다.</>,
                ]} />
                <p className="text-dc-ink3 text-[11.5px] mt-2 leading-snug">
                  받는 알림: 🏦큰손 수급(외국인·기관·연기금) · ⏩큰손 매수/매도 시작 · 📌내 핀 종목 손절·목표·이탈 · 👁감시 종목
                </p>
              </div>

              <p className="text-dc-ink3 text-[11.5px] leading-snug">
                알림이 안 오면 ① 설치된 앱 아이콘으로 열었는지 ② 단타 탭 🔔을 눌러 "허용"했는지 ③ 아이폰이면 Safari로 설치했는지 ④ 폰 설정&gt;알림에서 줍줍이 켜져 있는지 확인하세요.
              </p>
            </div>
          )}
        </div>

        <p className="text-center text-dc-ink3 text-[11.5px] pt-1">
          줍줍은 투자 참고용 도구입니다 · 매매 판단과 책임은 본인에게 있어요 · 면허 있는 투자자문이 아닙니다.
        </p>
      </div>
    </div>
  );
}
